import { extendVariants, Textarea } from "@nextui-org/react";
import { X as XIcon } from "lucide-react";
import { ComponentProps, useCallback, useMemo } from "react";

const CLEAR_BUTTON_BASE_CLASS_NAMES = "absolute top-2 right-2 z-10 p-0 bg-transparent border-none cursor-pointer text-foreground transition-opacity";
const CLEAR_BUTTON_VISIBLE_CLASS_NAMES = "opacity-70 hover:opacity-100 pointer-events-auto";
const CLEAR_BUTTON_HIDDEN_CLASS_NAMES = "opacity-0 pointer-events-none";

const BaseSpicyTextarea = extendVariants(Textarea, {
  variants: {
    default: {
      custom: {
        input: "focus:placeholder:opacity-0",
        base: "focus-within:translate-y-[-2px] transition-all",
        inputWrapper: "shadow-none group-data-[focus=true]:shadow-small",
      },
    },
    variant: {
      primary: {
        input: "text-[1rem] my-[8px] mx-[12px] leading-6",
        inputWrapper: "p-0",
      },
      primary_noEffect: {
        input: "text-[1rem] my-[8px] mx-[12px] leading-6",
        base: "focus-within:translate-y-0",
        inputWrapper: "p-0 group-data-[focus=true]:shadow-none",
      },
      secondary_noEffect: {
        input: "text-[1rem] my-[8px] mx-[12px] leading-6",
        base: "focus-within:translate-y-0 px-5",
        inputWrapper: "p-0 group-data-[focus=true]:shadow-none",
      },
    },
    color: {
      primary: {
        inputWrapper: "bg-gray-50 data-[hover=true]:bg-gray-50 group-data-[focus=true]:bg-gray-50",
      },
      secondary: {
        inputWrapper:
          "bg-white data-[hover=true]:bg-white group-data-[focus=true]:bg-white dark:bg-gray-50 dark:data-[hover=true]:bg-gray-50 dark:group-data-[focus=true]:bg-gray-50",
      },
      tertiary: {
        inputWrapper:
          "bg-gray-50 data-[hover=true]:bg-gray-50 group-data-[focus=true]:bg-gray-50 dark:bg-gray-50 dark:data-[hover=true]:bg-gray-50 dark:group-data-[focus=true]:bg-gray-50",
      },
      transparent: {
        inputWrapper:
          "bg-transparent data-[hover=true]:bg-transparent group-data-[focus=true]:bg-transparent dark:transparent dark:data-[hover=true]:transparent dark:group-data-[focus=true]:transparent border-1 border-solid border-foreground border-opacity-30 group-data-[focus=true]:border-opacity-60",
      },
    },
  },
  defaultVariants: {
    default: "custom",
  },
});

type BaseSpicyTextareaProps = ComponentProps<typeof BaseSpicyTextarea>;

type SpicyTextareaProps = Omit<BaseSpicyTextareaProps, "onClear"> & {
  onClear?: (() => void) | null;
};

const CLEAR_BUTTON_INPUT_PADDING = "!pr-8";

const SpicyTextarea = ({ onClear, value, endContent, classNames, ...props }: SpicyTextareaProps) => {
  const handleClear = useCallback(() => {
    onClear?.();
  }, [onClear]);

  const hasClearButton = Boolean(onClear && value);

  const mergedClassNames = useMemo<BaseSpicyTextareaProps["classNames"]>(() => {
    if (!onClear) return classNames;
    return {
      ...classNames,
      input: classNames?.input ? `${classNames.input} ${CLEAR_BUTTON_INPUT_PADDING}` : CLEAR_BUTTON_INPUT_PADDING,
    };
  }, [onClear, classNames]);

  const computedEndContent = useMemo(() => {
    if (!onClear) return endContent;
    const visibilityClass = hasClearButton ? CLEAR_BUTTON_VISIBLE_CLASS_NAMES : CLEAR_BUTTON_HIDDEN_CLASS_NAMES;
    return (
      <button
        type="button"
        aria-label="clear input"
        aria-hidden={!hasClearButton}
        tabIndex={hasClearButton ? undefined : -1}
        className={`${CLEAR_BUTTON_BASE_CLASS_NAMES} ${visibilityClass}`}
        onClick={handleClear}
      >
        <XIcon size={20} strokeWidth={2.5} />
      </button>
    );
  }, [onClear, hasClearButton, handleClear, endContent]);

  return <BaseSpicyTextarea {...props} value={value} classNames={mergedClassNames} endContent={computedEndContent} />;
};

export default SpicyTextarea;
